// src/pages/Users/Galleries/CreateAlbumModal.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaCheck,
  FaEye,
  FaEyeSlash,
  FaImages,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { addGallery } from "../../../services/gallerieServices";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

const INITIAL_FORM = {
  titre: "",
  description: "",
  is_active: true,
};

function stripHtml(html = "") {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
}

function normalizeRichText(value = "") {
  const html = String(value || "").trim();
  const plain = stripHtml(html);

  return plain ? html : "";
}

export default function CreateAlbumModal({ isOpen, onClose, refreshList }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  const titleRef = useRef(null);

  const plainDescription = useMemo(
    () => stripHtml(formData.description),
    [formData.description]
  );

  const isDirty = useMemo(() => {
    return (
      formData.titre !== INITIAL_FORM.titre ||
      formData.description !== INITIAL_FORM.description ||
      formData.is_active !== INITIAL_FORM.is_active
    );
  }, [formData]);

  const canSubmit = formData.titre.trim().length > 0 && isDirty && !saving;

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM);
    setSaving(false);
  }, []);

  const safeClose = useCallback(() => {
    if (saving) return;

    if (
      !isDirty ||
      window.confirm("Des informations ne sont pas enregistrées. Quitter ?")
    ) {
      onClose?.();
      setTimeout(resetForm, 0);
    }
  }, [isDirty, onClose, resetForm, saving]);

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") safeClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    const timer = setTimeout(() => {
      titleRef.current?.focus();
    }, 80);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timer);
    };
  }, [isOpen, safeClose]);

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen, resetForm]);

  if (!isOpen) return null;

  const handleChangeField = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleChangeContent = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const toggleVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      is_active: !prev.is_active,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const titre = formData.titre.trim();

    if (!titre) {
      toast.warn("Le titre de l’album est obligatoire.");
      titleRef.current?.focus();
      return;
    }

    try {
      setSaving(true);

      const payload = {
        titre,
        description: normalizeRichText(formData.description) || null,
        is_active: !!formData.is_active,
      };

      await addGallery(payload);

      toast.success("Album créé avec succès.");
      await refreshList?.();

      onClose?.();
      resetForm();
    } catch (error) {
      console.error("Erreur création album :", error);
      toast.error("Erreur lors de la création de l’album.");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/70 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Créer un album"
      onClick={(event) => {
        if (event.target === event.currentTarget) safeClose();
      }}
    >
      <article
        className="absolute inset-x-0 bottom-0 flex max-h-[94dvh] flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:relative sm:inset-auto sm:w-full sm:max-w-3xl sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="relative overflow-hidden bg-gradient-to-r from-slate-950 via-blue-900 to-violet-800 px-5 py-5 text-white sm:px-6">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/15 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ring-1 ring-white/20">
                <FaImages />
                Nouvel album
              </span>

              <h2 className="mt-3 text-2xl font-bold leading-tight">
                Créer un album photo
              </h2>

              <p className="mt-1 text-sm leading-6 text-white/80">
                Ajoute un album visible dans la galerie du club. Tu pourras
                ensuite y téléverser les photos.
              </p>
            </div>

            <button
              type="button"
              onClick={safeClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
              aria-label="Fermer"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5 sm:p-6"
        >
          <div className="space-y-5">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                Titre de l’album *
              </label>

              <input
                ref={titleRef}
                type="text"
                name="titre"
                maxLength={120}
                placeholder="Ex. Tournoi régional — Janvier 2026"
                value={formData.titre}
                onChange={handleChangeField}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                required
              />

              <div className="mt-1 flex justify-between text-[11px] text-slate-500 dark:text-slate-400">
                <span>Nom affiché sur la carte de l’album.</span>
                <span>{formData.titre.length}/120</span>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                Description
              </label>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                <ReactQuill
                  value={formData.description}
                  onChange={handleChangeContent}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Décris l’événement : lieu, date, résultats, remerciements..."
                  className="bg-white text-slate-900 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                {plainDescription.length} caractère
                {plainDescription.length > 1 ? "s" : ""} en texte brut.
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    Visibilité de l’album
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Un album visible apparaît dans la galerie pour les
                    adhérents. Un album masqué reste accessible à la gestion.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={toggleVisibility}
                  className={`relative inline-flex h-8 w-[62px] shrink-0 items-center rounded-full p-1 transition ${
                    formData.is_active
                      ? "bg-emerald-500"
                      : "bg-slate-300 dark:bg-slate-700"
                  }`}
                  aria-pressed={formData.is_active}
                  aria-label="Basculer la visibilité"
                >
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full bg-white text-[11px] shadow transition ${
                      formData.is_active ? "translate-x-[30px]" : "translate-x-0"
                    }`}
                  >
                    {formData.is_active ? (
                      <FaEye className="text-emerald-600" />
                    ) : (
                      <FaEyeSlash className="text-slate-500" />
                    )}
                  </span>
                </button>
              </div>

              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                {formData.is_active ? (
                  <>
                    <FaCheck className="text-emerald-500" />
                    Album visible
                  </>
                ) : (
                  <>
                    <FaEyeSlash className="text-slate-400" />
                    Album masqué
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 -mx-5 mt-6 border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:-mx-6 sm:px-6">
            <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
              <button
                type="button"
                onClick={safeClose}
                disabled={saving}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 text-sm font-bold text-slate-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 text-sm font-bold text-white transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950"
              >
                <FaSave />
                {saving ? "Création..." : "Créer"}
              </button>
            </div>

            <p className="mt-3 text-center text-[11px] text-slate-500 dark:text-slate-400 sm:text-right">
              {saving
                ? "Création de l’album en cours..."
                : isDirty
                ? "Modifications non enregistrées."
                : "Renseigne au moins un champ pour créer un album."}
            </p>
          </div>
        </form>
      </article>
    </div>,
    document.body
  );
}